import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Btn, Card, Label, Input, Select } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ListChecks, Plus, Pencil, Trash2, Camera, X } from "lucide-react";

const SERVICE_TYPES = ["", "Property Preservation", "Initial Services", "Inspection", "Occupancy Check", "Eviction", "REO Services", "Maid Refresh / Janitorial", "Lock Changes / Re-Key", "Debris Removal", "Winterization", "De-Winterization", "Grass Cut / Lawn Maintenance", "Securing / Board-Up", "Pool Securing", "Repairs"];
const blank = { name: "", service_type: "", keywords: "", items: [{ label: "", requires_photo: true }] };

export default function Checklists() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => api.get("/checklist-templates").then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEdit({ ...blank, items: [{ label: "", requires_photo: true }] });
  const openEdit = (t) => setEdit({ id: t.id, name: t.name, service_type: t.service_type || "", keywords: (t.keywords || []).join(", "), items: (t.items || []).map((i) => ({ ...i })) });

  const save = async () => {
    if (!edit.name.trim()) { toast.error("Name required"); return; }
    const items = edit.items.filter((i) => i.label.trim()).map((i) => ({ label: i.label.trim(), requires_photo: !!i.requires_photo }));
    if (!items.length) { toast.error("Add at least one item"); return; }
    const payload = { name: edit.name.trim(), service_type: edit.service_type, keywords: edit.keywords.split(",").map((k) => k.trim()).filter(Boolean), items };
    try {
      if (edit.id) await api.put(`/checklist-templates/${edit.id}`, payload);
      else await api.post("/checklist-templates", payload);
      toast.success("Checklist saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (t) => { if (!window.confirm(`Delete checklist "${t.name}"?`)) return; try { await api.delete(`/checklist-templates/${t.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const setItem = (i, patch) => setEdit((e) => ({ ...e, items: e.items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) }));
  const addItem = () => setEdit((e) => ({ ...e, items: [...e.items, { label: "", requires_photo: true }] }));
  const rmItem = (i) => setEdit((e) => ({ ...e, items: e.items.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Scope-of-Work Procedures</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">Checklists</h1>
          <p className="text-zinc-500 text-sm mt-1 font-mono">Reusable photo/task checklists. Auto-attach to work orders when the service type or scope keywords match.</p>
        </div>
        <Btn data-testid="add-checklist-btn" onClick={openNew}><Plus size={16} /> New Checklist</Btn>
      </div>

      {list.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono"><ListChecks className="inline mr-2" size={16} />No checklists yet.</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((t) => (
            <Card key={t.id} data-testid={`checklist-${t.id}`} className="p-5 flex flex-col">
              <div className="w-11 h-11 bg-[#FF5722] text-black border-2 border-black shadow-[2px_2px_0px_#000] flex items-center justify-center mb-3"><ListChecks size={20} /></div>
              <div className="font-head font-bold text-white text-lg leading-tight">{t.name}</div>
              {t.service_type && <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722] mt-0.5">{t.service_type}</div>}
              <ul className="mt-2 space-y-1 flex-1">
                {(t.items || []).slice(0, 6).map((it, i) => (
                  <li key={i} className="text-sm text-zinc-300 flex items-center gap-1.5"><span className="text-zinc-600">•</span>{it.label}{it.requires_photo && <Camera size={11} className="text-emerald-400 shrink-0" />}</li>
                ))}
                {(t.items || []).length > 6 && <li className="font-mono text-[10px] text-zinc-600">+{t.items.length - 6} more…</li>}
              </ul>
              <div className="mt-4 pt-3 border-t border-zinc-800 flex gap-2">
                <Btn variant="secondary" data-testid={`edit-checklist-${t.id}`} onClick={() => openEdit(t)} className="flex-1"><Pencil size={14} /> Edit</Btn>
                <Btn variant="danger" data-testid={`del-checklist-${t.id}`} onClick={() => del(t)}><Trash2 size={14} /></Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="checklist-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head uppercase">{edit?.id ? "Edit Checklist" : "New Checklist"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="checklist-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. Winterization" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Auto-attach for service type</Label><Select data-testid="checklist-service" value={edit.service_type} onChange={(e) => setEdit({ ...edit, service_type: e.target.value })}>{SERVICE_TYPES.map((s) => <option key={s} value={s}>{s || "— Any —"}</option>)}</Select></div>
                <div><Label>Scope keywords (comma-sep)</Label><Input data-testid="checklist-keywords" value={edit.keywords} onChange={(e) => setEdit({ ...edit, keywords: e.target.value })} placeholder="winterize, antifreeze" /></div>
              </div>
              <div>
                <Label>Items (toggle camera = photo required)</Label>
                <div className="space-y-2">
                  {edit.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input data-testid={`checklist-item-${i}`} value={it.label} onChange={(e) => setItem(i, { label: e.target.value })} placeholder="Checklist item…" className="flex-1" />
                      <button type="button" data-testid={`checklist-photo-toggle-${i}`} onClick={() => setItem(i, { requires_photo: !it.requires_photo })} title="Photo required" className={`w-9 h-9 border-2 flex items-center justify-center shrink-0 ${it.requires_photo ? "border-emerald-600 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}><Camera size={14} /></button>
                      <button type="button" onClick={() => rmItem(i)} className="text-zinc-500 hover:text-red-400 shrink-0"><X size={16} /></button>
                    </div>
                  ))}
                </div>
                <Btn variant="ghost" data-testid="add-item-btn" onClick={addItem} className="mt-2"><Plus size={14} /> Add item</Btn>
              </div>
              <div className="flex justify-end gap-2 pt-2"><Btn variant="secondary" onClick={() => setEdit(null)}>Cancel</Btn><Btn data-testid="save-checklist-btn" onClick={save}>Save</Btn></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
